import { useEffect, useState } from 'react';
import { tokens, Link } from '@hotjar/hotsauce';
import styled from '@emotion/styled';
import { useFormContext } from 'react-hook-form';
import type { FieldErrors } from 'react-hook-form';
import { setupPaypal } from '@hotjar/checkout/src/braintree/paypal';
import { useOwnedAccount } from '@hotjar/account';
import { useTranslationWithPrefix } from '@hotjar/translations';
import {
  PAYMENT_FORM_SUBMITTED_ERRORS,
  trackPaymentFormSubmitted,
} from '@hotjar/checkout/src/utils/tracking';
import type { BillingCycleFormField } from '@hotjar/checkout/src/PaymentDetails/components/BillingCycleSelector';
import { VaultedPaymentMethod } from '@hotjar/webapp/billing/payment';

import * as CommonStyles from '../styles';
import { PayPalLogo } from '../../assets/PayPalLogo';

export const PAYPAL_FORM_ID = 'paypal-form';
const BUTTON_HEIGHT = 40;
const PAYPAL_BUTTON_ID = 'modal-payment-form-paypal-button';
const S = {
  SectionContainer: styled(CommonStyles.SectionContainer)`
    padding: ${tokens.spacer.x2};
  `,
  ButtonContainer: styled.div`
    width: 200px;
    height: ${BUTTON_HEIGHT}px;
  `,
  PayPalInfo: styled.div`
    display: grid;
    grid-template-columns: min-content auto min-content;
    align-items: center;
    grid-gap: ${tokens.spacer.x2};
  `,
  PayPalEmail: styled.span`
    ${tokens.truncateText()};
  `,
};

interface Props {
  visible: boolean;
  onSubmit: () => void;
  onSuccess(vaultedPaymentMethod: VaultedPaymentMethod): void;
}

export function PayPal({ visible, onSuccess, onSubmit }: Props): React.ReactElement | null {
  const [paypalToken, setPayPalToken] = useState<paypal.AuthorizationResponse>();
  const { data: account } = useOwnedAccount();
  const { t } = useTranslationWithPrefix('common:actions');
  const { handleSubmit: handleFormSubmit } = useFormContext<BillingCycleFormField>();

  function intializePayPalButton() {
    setPayPalToken(undefined);
    if (account) {
      setupPaypal({
        accountId: account.id,
        paypalButtonId: 'modal-payment-form-paypal-button',
        onPaypalLogin: setPayPalToken,
        style: { color: 'blue', label: 'checkout', height: BUTTON_HEIGHT },
      });
    }
  }

  useEffect(intializePayPalButton, [account]);

  function handleSubmit(isValid: boolean) {
    onSubmit();
    if (!isValid || !paypalToken) {
      return;
    }

    const paypalPaymentMethod = new VaultedPaymentMethod(paypalToken.nonce, undefined, 'paypal');
    onSuccess(paypalPaymentMethod);
  }

  function handleInvalidState({ billingCycle }: FieldErrors<BillingCycleFormField>) {
    if (billingCycle) {
      trackPaymentFormSubmitted(account, false, PAYMENT_FORM_SUBMITTED_ERRORS.billingCycle);
    }
    handleSubmit(!billingCycle);
  }

  return (
    // We're hiding this using CSS (instead of not rendering at all), so
    //  Braintree's buttons are not destroyed when toggling between payment methods
    <S.SectionContainer style={{ display: visible ? 'block' : 'none' }}>
      <form
        onSubmit={(event) => {
          handleFormSubmit(() => handleSubmit(true), handleInvalidState)(event);
        }}
        id={PAYPAL_FORM_ID}
      >
        {!paypalToken && <S.ButtonContainer id={PAYPAL_BUTTON_ID} />}
        {!!paypalToken && (
          <S.PayPalInfo>
            <PayPalLogo /> <S.PayPalEmail>{paypalToken.details.email}</S.PayPalEmail>
            <span>
              <Link kind="inline" as="button" onClick={intializePayPalButton}>
                {t('cancel')}
              </Link>
            </span>
          </S.PayPalInfo>
        )}
      </form>
    </S.SectionContainer>
  );
}
